import { createContext, useState, useEffect, useRef } from 'react';

const DISCORD_USER_ID = '138346196996456448';
const LANYARD_WS = 'wss://api.lanyard.rest/socket';

export const DEFAULT_PRESENCE = {
  status: 'offline',
  activity: {
    type: 'idle',
    name: 'Offline',
    detail: '',
    elapsed: '',
    source: 'discord',
    largeImageUrl: null,
  },
};

/* ── Elapsed time formatter ──────────────────────────────────────────────── */
function formatElapsed(startMs) {
  if (!startMs) return '';
  const s = Math.floor((Date.now() - startMs) / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60) % 60;
  const h = Math.floor(s / 3600);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/* ── Coding app detection ────────────────────────────────────────────────── */
const CODING_APPS = [
  'visual studio code', 'vscode', 'cursor', 'neovim', 'nvim', 'vim',
  'emacs', 'sublime text', 'jetbrains', 'intellij', 'webstorm', 'pycharm',
  'rider', 'clion', 'android studio', 'xcode', 'zed',
];
function isCodingApp(name) {
  const lower = (name ?? '').toLowerCase();
  return CODING_APPS.some(app => lower.includes(app));
}

/* ── Map raw Lanyard payload → internal presence shape ───────────────────── */
function mapLanyard(data) {
  const status = data.discord_status ?? 'offline';

  // 1. Spotify
  if (data.listening_to_spotify && data.spotify) {
    const sp = data.spotify;
    return {
      status,
      activity: {
        type: 'listening',
        name: sp.song ?? 'Unknown',
        detail: sp.artist ?? '',
        elapsed: formatElapsed(sp.timestamps?.start),
        source: 'discord',
        largeImageUrl: sp.album_art_url ?? null,
      },
    };
  }

  // 2. Voice / video call — Discord's native DM/group-DM calls are type-0 activities
  //    with no application_id (third-party games always have one) and a name of "Call".
  //    Checking application_id prevents mis-classifying games like "Call of Duty".
  const voiceCall = (data.activities ?? []).find(a =>
    a.type === 0 &&
    !a.application_id &&
    /^call$/i.test(a.name ?? '')
  );
  if (voiceCall) {
    return {
      status,
      activity: {
        type: 'call',
        name: voiceCall.name ?? 'Voice Call',
        detail: voiceCall.details ?? voiceCall.state ?? '',
        elapsed: formatElapsed(voiceCall.timestamps?.start),
        source: 'discord',
        largeImageUrl: null,
      },
    };
  }

  // 3. Game / IDE activity (type 0)
  const game = (data.activities ?? []).find(a => a.type === 0);
  if (game) {
    return {
      status,
      activity: {
        type: isCodingApp(game.name) ? 'coding' : 'playing',
        name: game.name ?? 'a game',
        detail: game.details ?? game.state ?? '',
        elapsed: formatElapsed(game.timestamps?.start),
        source: 'discord',
        largeImageUrl: null,
      },
    };
  }

  // 4. Custom status (type 4)
  const custom = (data.activities ?? []).find(a => a.type === 4);
  if (custom && (custom.state || custom.emoji?.name)) {
    return {
      status,
      activity: {
        type: 'idle',
        name: custom.state ?? custom.emoji?.name ?? 'Custom status',
        detail: '',
        elapsed: '',
        source: 'discord',
        largeImageUrl: null,
      },
    };
  }

  // 5. Fallback
  const fallbackName =
    status === 'offline' ? 'Offline'
    : status === 'idle'  ? 'Away'
    : 'Online';

  return {
    status,
    activity: {
      type: 'idle',
      name: fallbackName,
      detail: '',
      elapsed: '',
      source: 'discord',
      largeImageUrl: null,
    },
  };
}

/* ── Context ─────────────────────────────────────────────────────────────── */
export const PresenceContext = createContext(null);

/* ── Provider — owns the single shared WebSocket ────────────────────────── */
export function PresenceProvider({ children }) {
  const [presence, setPresence] = useState(DEFAULT_PRESENCE);
  const [loading, setLoading]   = useState(true);

  const rawRef       = useRef(null);
  const wsRef        = useRef(null);
  const heartbeatRef = useRef(null);

  // Tick elapsed time every 30 s without re-fetching
  useEffect(() => {
    const id = setInterval(() => {
      if (rawRef.current) setPresence(mapLanyard(rawRef.current));
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  // Single WebSocket with auto-reconnect
  useEffect(() => {
    let cancelled = false;

    function connect() {
      const ws = new WebSocket(LANYARD_WS);
      wsRef.current = ws;

      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);

        if (msg.op === 1) {
          ws.send(JSON.stringify({ op: 2, d: { subscribe_to_id: DISCORD_USER_ID } }));
          heartbeatRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ op: 3 }));
          }, msg.d.heartbeat_interval);
        }

        if (msg.op === 0 && msg.d) {
          rawRef.current = msg.d;
          setPresence(mapLanyard(msg.d));
          setLoading(false);
        }
      };

      ws.onclose = () => {
        clearInterval(heartbeatRef.current);
        if (!cancelled) setTimeout(connect, 5_000);
      };

      ws.onerror = () => ws.close();
    }

    connect();

    return () => {
      cancelled = true;
      clearInterval(heartbeatRef.current);
      wsRef.current?.close();
    };
  }, []);

  return (
    <PresenceContext.Provider value={{ presence, loading }}>
      {children}
    </PresenceContext.Provider>
  );
}
